import type { PricingSettings } from '@/types'

// F-MM-N29: mirrors backend pricing.Defaults — no service charge unless the
// tenant configured one (a guessed 10% overcharged NinetyPlus and Karo), tax
// 15 / 5 / 5.
const DEFAULTS: PricingSettings = {
  tax_rate_cash: 0.15,
  tax_rate_card: 0.05,
  tax_rate_online: 0.05,
  service_charge_rate: 0,
  tenant_jurisdiction: '',
  pra_rims_live: false,
  delivery_excluded_from_tax_base: false,
  service_charge_excluded_from_tax_base: false,
  service_charge_on_gross_subtotal: false,
  pos_service_fee: 0,
}

function round2(n: number): number {
  return Math.round(n * 100) / 100
}

export function mergePricingSettings(p?: Partial<PricingSettings> | null): PricingSettings {
  return {
    tax_rate_cash: p?.tax_rate_cash ?? DEFAULTS.tax_rate_cash,
    tax_rate_card: p?.tax_rate_card ?? DEFAULTS.tax_rate_card,
    tax_rate_online: p?.tax_rate_online ?? DEFAULTS.tax_rate_online,
    service_charge_rate: p?.service_charge_rate ?? DEFAULTS.service_charge_rate,
    tenant_jurisdiction: p?.tenant_jurisdiction ?? DEFAULTS.tenant_jurisdiction,
    pra_rims_live: p?.pra_rims_live ?? DEFAULTS.pra_rims_live,
    delivery_excluded_from_tax_base:
      p?.delivery_excluded_from_tax_base ?? DEFAULTS.delivery_excluded_from_tax_base,
    service_charge_excluded_from_tax_base:
      p?.service_charge_excluded_from_tax_base ?? DEFAULTS.service_charge_excluded_from_tax_base,
    service_charge_on_gross_subtotal:
      p?.service_charge_on_gross_subtotal ?? DEFAULTS.service_charge_on_gross_subtotal,
    // F-MM-N9: already resolved server-side against the enable flag and the
    // tenant's fiscal authority, so it is 0 for PRA and opted-out tenants.
    // The counter applies no gate of its own — it just renders what it is told.
    pos_service_fee: p?.pos_service_fee ?? DEFAULTS.pos_service_fee,
    // Pass through the active online sub-methods (migration 050). Does not
    // affect any tax math — computeCartTotals still keys purely on the coarse
    // 'online' intent — it just rides along for the tender pad to render.
    online_submethods: p?.online_submethods ?? [],
  }
}

export interface OrderPricingExtras {
  /** When false, service amount is 0 (global % still in Financial for dine-in, etc.). */
  includeServiceCharge?: boolean
  /** Flat delivery add-on. Included in tax base unless DeliveryExcludedFromTaxBase is true. */
  deliveryFee?: number
  /**
   * Sum of per-line manual discounts on the order. Reduces the taxable
   * base alongside the bill-level `discount`. Stacks with `discount` —
   * the cashier may apply both. Defaults to 0.
   */
  itemDiscountsTotal?: number
  /**
   * Per-order service charge override (2026-08-19, mirrors backend
   * pricing.ServiceCharge via orders.service_charge_override_rate/_amount).
   * `serviceChargeRateOverride` is a fraction (0.05 == 5%) that replaces the
   * settings rate for this order and still scales with the bill.
   * `serviceChargeAmountOverride` is a fixed figure that replaces the
   * percentage outright; `serviceRate` is then reported as 0 so no "(X%)"
   * label is rendered beside a number that is not X% of anything. Amount
   * wins if both are set. Either override is honoured even when
   * `includeServiceCharge` is false — setting it is an explicit cashier
   * action. null / undefined = no override.
   */
  serviceChargeRateOverride?: number | null
  serviceChargeAmountOverride?: number | null
  /**
   * F-MM-N23. 'marketplace_settled' = the aggregator remits sales tax, so the
   * POS charges tax 0 and FBR POS fee 0 on every intent. Service charge and
   * delivery are untouched. Mirrors backend pricing for marketplace types.
   * Defaults to 'standard'.
   */
  taxTreatment?: 'standard' | 'marketplace_settled'
}

export function computeCartTotals(
  subtotal: number,
  discount: number,
  intent: 'cash' | 'card' | 'online',
  p: PricingSettings,
  extras?: OrderPricingExtras
) {
  const includeSvc = extras?.includeServiceCharge !== false
  const taxTreatment: 'standard' | 'marketplace_settled' =
    extras?.taxTreatment === 'marketplace_settled' ? 'marketplace_settled' : 'standard'
  const marketplace = taxTreatment === 'marketplace_settled'
  const deliveryFee = round2(Math.max(0, extras?.deliveryFee ?? 0))
  const itemDiscounts = round2(Math.max(0, extras?.itemDiscountsTotal ?? 0))
  const taxable = Math.max(0, subtotal - discount - itemDiscounts)
  // Service charge resolution — amount override > rate override > settings
  // (gated by the order type's include flag). Mirrors backend
  // handlers.effectiveServiceCharge + pricing.ServiceCharge.Resolve.
  const amountOv = extras?.serviceChargeAmountOverride
  const rateOv = extras?.serviceChargeRateOverride
  let effServiceRate: number
  let service: number
  if (amountOv != null && Number.isFinite(amountOv)) {
    effServiceRate = 0
    service = round2(Math.max(0, amountOv))
  } else {
    effServiceRate =
      rateOv != null && Number.isFinite(rateOv)
        ? rateOv
        : includeSvc
          ? p.service_charge_rate
          : 0
    // Mirrors backend pricing.computeTotalsCore. Post-discount by default;
    // gross only where the tenant opted in, because the charge is their
    // waiters' pool and a promotion is the owner's cost, not the staff's.
    // A fully comped bill (taxable 0) never carries a service charge on either
    // basis — a percentage of the undiscounted menu price would make the
    // "service charge" the whole bill for food that was given away.
    const serviceBase =
      p.service_charge_on_gross_subtotal && taxable > 0 ? subtotal : taxable
    service = round2(serviceBase * effServiceRate)
  }

  // Tax base (post-2026-05): includes service charge and delivery by
  // default. Tenants who have explicitly opted out flip the corresponding
  // setting. Mirrors backend pricing.ComputeTotalsEx. Per PSTSA 2012
  // "Value of Service" and Punjab Finance Bill 2025.
  let taxBase = taxable
  if (!p.service_charge_excluded_from_tax_base) taxBase += service
  if (!p.delivery_excluded_from_tax_base) taxBase += deliveryFee

  // Rate is purely the configured per-intent rate. Mirrors backend
  // pricing.TaxRateForCheckoutIntent. The Punjab/RIMS gate that used to
  // override card+online down to the cash rate was removed 2026-08-03 —
  // it made the counter disagree with the Settings screen with no
  // indication why. A tenant charges what it is configured to charge.
  //
  // F-MM-N23: a marketplace-settled order carries no sales tax at all — the
  // platform remits it — so the rate is 0 on every intent. This is a
  // treatment, not a jurisdiction gate: it is set per custom order type by
  // the tenant and never changes a configured rate for standard types.
  const tr = marketplace
    ? 0
    : intent === 'card'
      ? p.tax_rate_card
      : intent === 'online'
        ? p.tax_rate_online
        : p.tax_rate_cash

  const tax = round2(taxBase * tr)

  // F-MM-N9: the FBR POS service fee. Collected on FBR's behalf rather than
  // being consideration for a supply, so it is deliberately absent from
  // taxBase above and enters only the amount the customer pays. Mirrors
  // backend pricing.ComputeTotalsEx — if these two disagree, the counter shows
  // one price and the backend charges another.
  //
  // Flat per invoice: it does not scale with the bill, does not vary by
  // intent, and is not skipped on a fully-discounted order.
  // Not filed with FBR, so no FBR fee either on a marketplace-settled order.
  const posFee = marketplace ? 0 : round2(Math.max(0, p.pos_service_fee ?? 0))

  const total = round2(taxable + service + tax + deliveryFee + posFee)
  // `taxRate` and `serviceRate` are returned as fractions (0.16 == 16%) so
  // the caller can render a consistent "Sales Tax (16%)" / "Service Charges
  // (10%)" label alongside the money amount.
  return {
    taxable,
    service,
    tax,
    posFee,
    total,
    taxRate: tr,
    serviceRate: effServiceRate,
    delivery: deliveryFee,
    itemDiscounts,
    taxTreatment,
  }
}
